import React, { useState, useEffect } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import Modal from './ui/Modal';
import ConfirmationModal from './ui/ConfirmationModal';
import { API_CONFIG } from '../config';
import { ICONS } from '../constants';
import { PIPELINE_SPECIALIZATIONS } from '../constants/biddingPipeline';

interface Artist {
  id: string;
  name: string;
  specialization?: string;
  hourlyRate?: number;
}

interface CustomParticipant {
  id: string;
  name: string;
  color: string;
  biddingSheetId: string;
}

interface User {
  id: string;
  username: string;
  email: string;
  role: string;
}

interface ArtistRatesModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId?: string;
  biddingSheetId?: string;
  existingSpecializations: string[];
  onSpecializationsChange: (specs: string[]) => void;
  producerPercentage: number;
  coordinatorFee: number;
  supervisorFee: number;
  vatRate: number;
  onProducerPercentageChange: (value: number) => void;
  onCoordinatorFeeChange: (value: number) => void;
  onSupervisorFeeChange: (value: number) => void;
  onVatRateChange: (value: number) => void;
  currency?: string;
  producerName?: string;
  supervisorName?: string;
  onProducerNameChange?: (value: string) => void;
  onSupervisorNameChange?: (value: string) => void;
  onRatesSaved?: () => void; // Callback to refresh assignments after rates are saved
  activeTable?: 'revenue' | 'expense'; // Active table to determine which artists to show
}

const ArtistRatesModal: React.FC<ArtistRatesModalProps> = ({
  isOpen,
  onClose,
  projectId,
  biddingSheetId,
  existingSpecializations,
  onSpecializationsChange,
  producerPercentage,
  coordinatorFee,
  supervisorFee,
  vatRate,
  onProducerPercentageChange,
  onCoordinatorFeeChange,
  onSupervisorFeeChange,
  onVatRateChange,
  currency = 'USD',
  producerName = '',
  supervisorName = '',
  onProducerNameChange,
  onSupervisorNameChange,
  onRatesSaved,
  activeTable = 'revenue'
}) => {
  const { t } = useTranslation();
  const [artists, setArtists] = useState<Artist[]>([]);
  const [customParticipants, setCustomParticipants] = useState<CustomParticipant[]>([]);
  const [newParticipantName, setNewParticipantName] = useState('');
  const [loading, setLoading] = useState(false);
  // Matrix structure: rates[artistId][specialization] = hourlyRate
  const [rates, setRates] = useState<Record<string, Record<string, number>>>({});
  const [specializations, setSpecializations] = useState<string[]>(existingSpecializations);
  const [newSpecialization, setNewSpecialization] = useState('');
  const [showAddSpec, setShowAddSpec] = useState(false);
  const [producers, setProducers] = useState<User[]>([]);
  const [supervisors, setSupervisors] = useState<User[]>([]);
  const [coordinators, setCoordinators] = useState<User[]>([]);
  const [selectedProducerId, setSelectedProducerId] = useState<string>('');
  const [selectedSupervisorId, setSelectedSupervisorId] = useState<string>('');
  const [selectedCoordinatorId, setSelectedCoordinatorId] = useState<string>('');
  const [producerDisplayName, setProducerDisplayName] = useState<string>('');
  const [supervisorDisplayName, setSupervisorDisplayName] = useState<string>('');
  // Global rates for comparison (color-coding)
  const [globalRates, setGlobalRates] = useState<Record<string, Record<string, number>>>({});
  
  // Confirmation modals
  const [deleteParticipantModal, setDeleteParticipantModal] = useState<{ isOpen: boolean; participantId: string }>({ isOpen: false, participantId: '' });
  const [deleteSpecModal, setDeleteSpecModal] = useState<{ isOpen: boolean; specName: string }>({ isOpen: false, specName: '' });

  useEffect(() => {
    if (isOpen && projectId) {
      fetchArtists();
      fetchProjectAndUsers();
      fetchGlobalRates();
      if (biddingSheetId) {
        fetchCustomParticipants();
      }
    }
  }, [isOpen, projectId, biddingSheetId, activeTable]);

  const fetchGlobalRates = async () => {
    try {
      const token = localStorage.getItem('spellbook_token');
      const response = await fetch(`${API_CONFIG.API_URL}/rates/virtual`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // Convert to format: globalRates[virtualType][specialization] = hourlyRate
        const ratesMap: Record<string, Record<string, number>> = {};
        for (const rate of data.rates) {
          const virtualKey = `virtual-${rate.virtualArtistType}`;
          if (!ratesMap[virtualKey]) {
            ratesMap[virtualKey] = {};
          }
          ratesMap[virtualKey][rate.specialization] = rate.hourlyRate;
        }
        setGlobalRates(ratesMap);
        console.log('🌐 Global rates loaded:', ratesMap);
      }
    } catch (error) {
      console.error('Error fetching global rates:', error);
    }
  };

  // Helper to check if rate is custom (differs from global)
  const isCustomRate = (artistId: string, specialization: string, currentRate: number): boolean => {
    if (!artistId.startsWith('virtual-')) return false; // Only for virtual artists
    const globalRate = globalRates[artistId]?.[specialization];
    if (globalRate === undefined) return false;
    return Math.abs(currentRate - globalRate) > 0.001; // Allow small floating point differences
  };

  // Get input class with color-coding
  const getRateInputClass = (artistId: string, specialization: string, currentRate: number): string => {
    const baseClass = "w-20 px-2 py-1 bg-gray-800 rounded text-white text-center text-xs focus:outline-none";
    const isCustom = isCustomRate(artistId, specialization, currentRate);
    if (isCustom) {
      return `${baseClass} border-2 border-amber-500/70 ring-1 ring-amber-500/30`;
    }
    return `${baseClass} border border-gray-700 focus:border-indigo-500`;
  };

  // Sync specializations with prop changes
  useEffect(() => {
    setSpecializations(existingSpecializations);
  }, [existingSpecializations]);

  const fetchArtists = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('spellbook_token');
      
      let fetchedArtists: Artist[] = [];

      if (activeTable === 'revenue') {
        // For revenue table, show only virtual artists (junior, mid, senior, lead)
        fetchedArtists = [
          { id: 'virtual-junior', name: 'Junior Artist', specialization: 'Virtual' },
          { id: 'virtual-mid', name: 'Mid Artist', specialization: 'Virtual' },
          { id: 'virtual-senior', name: 'Senior Artist', specialization: 'Virtual' },
          { id: 'virtual-lead', name: 'Lead Artist', specialization: 'Virtual' }
        ];
      } else {
        // For expense table, fetch real artists from the project
        const artistsResponse = await fetch(`${API_CONFIG.API_URL}/projects/${projectId}/artists`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (!artistsResponse.ok) {
          console.error('Failed to fetch artists:', artistsResponse.status);
          return;
        }

        const artistsData = await artistsResponse.json();
        // Filter out virtual artists (role 'Virtual Artist' or specific names)
        fetchedArtists = (artistsData.artists || []).filter((a: any) => 
          a.role !== 'Virtual Artist' && 
          !a.name.includes('Compositor Mid') && 
          !a.name.includes('Compositor Lead')
        );
      }
      
      setArtists(fetchedArtists);

      // Fetch existing rates from database
      const ratesResponse = await fetch(`${API_CONFIG.API_URL}/projects/${projectId}/rates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      let existingRates: Array<{artistId: string, specialization: string, hourlyRate: number}> = [];
      if (ratesResponse.ok) {
        const ratesData = await ratesResponse.json();
        existingRates = ratesData.rates || [];
      }

      // Initialize rates matrix: rates[artistId][specialization] = hourlyRate
      const initialRates: Record<string, Record<string, number>> = {};
      
      fetchedArtists.forEach((artist: Artist) => {
        initialRates[artist.id] = {};
        
        // For each specialization, check if there's an existing rate
        specializations.forEach(spec => {
          const existingRate = existingRates.find(
            r => r.artistId === artist.id && r.specialization === spec
          );
          initialRates[artist.id][spec] = existingRate ? existingRate.hourlyRate : 0;
        });
      });

      console.log('📊 Initial rates matrix:', initialRates);
      setRates(initialRates);
    } catch (error) {
      console.error('Error fetching artists and rates:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectAndUsers = async () => {
    try {
      const token = localStorage.getItem('spellbook_token');
      
      // Fetch all users first
      const usersResponse = await fetch(`${API_CONFIG.API_URL}/vfx/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      let allUsers: User[] = [];
      if (usersResponse.ok) {
        const usersData = await usersResponse.json();
        // VFX API returns data in "data" field, not "users"
        allUsers = usersData.data || usersData.users || [];
        
        console.log('📋 All users:', allUsers.map(u => ({ id: u.id, username: u.username, email: u.email, role: u.role })));
        
        // Filter different roles (case-insensitive comparison)
        const producersList = allUsers.filter((u: User) => {
          const roleLower = u.role?.toLowerCase();
          return roleLower === 'producer' || roleLower === 'admin' || roleLower === 'super admin' || roleLower === 'sales';
        });
        const supervisorsList = allUsers.filter((u: User) => {
          const roleLower = u.role?.toLowerCase();
          return roleLower === 'manager' || roleLower === 'project manager' || roleLower === 'producer' || roleLower === 'admin' || roleLower === 'super admin';
        });
        // Coordinators: ALL users can be selected as coordinators
        const coordinatorsList = allUsers;
        
        console.log('👔 Producers:', producersList.map(p => p.username));
        console.log('👨‍💼 Supervisors:', supervisorsList.map(s => s.username));
        console.log('📋 Coordinators (all users):', coordinatorsList.length);
        
        setProducers(producersList);
        setSupervisors(supervisorsList);
        setCoordinators(coordinatorsList);
      }

      // Fetch project info
      const projectResponse = await fetch(`${API_CONFIG.API_URL}/projects/${projectId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (projectResponse.ok) {
        const projectData = await projectResponse.json();
        console.log('📦 Project data:', projectData);
        
        // Set producer display from project
        setSelectedProducerId(projectData.producerId || '');
        const producer = allUsers.find(u => u.id === projectData.producerId);
        console.log('🎬 Producer ID:', projectData.producerId, 'Found:', producer);
        setProducerDisplayName(producer?.username || t('common.notSet'));
        
        // Set supervisor
        setSelectedSupervisorId(projectData.managerId || '');
        const supervisor = allUsers.find(u => u.id === projectData.managerId);
        console.log('👨‍💼 Supervisor ID:', projectData.managerId, 'Found:', supervisor);
        setSupervisorDisplayName(supervisor?.username || t('common.notSet'));
      }
      
      // Fetch bidding sheet to get saved producer and coordinator names
      if (biddingSheetId) {
        const biddingResponse = await fetch(`${API_CONFIG.API_URL}/bidding/${biddingSheetId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (biddingResponse.ok) {
          const biddingData = await biddingResponse.json();
          console.log('📋 Bidding sheet data:', biddingData);
          
          // If producer name is saved in bidding sheet, use it
          if (biddingData.producerName) {
            setProducerDisplayName(biddingData.producerName);
          }
          
          // Find coordinator by name in the users list
          if (biddingData.coordinatorName) {
            const coordinator = allUsers.find(u => u.username === biddingData.coordinatorName);
            if (coordinator) {
              setSelectedCoordinatorId(coordinator.id);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error fetching project and users:', error);
    }
  };

  const fetchCustomParticipants = async () => {
    try {
      const token = localStorage.getItem('spellbook_token');
      const response = await fetch(`${API_CONFIG.API_URL}/bidding/${biddingSheetId}/custom-participants`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const participants = await response.json();
        setCustomParticipants(participants);
        
        // Initialize rates for custom participants
        setRates(prev => {
          const updatedRates = { ...prev };
          participants.forEach((participant: CustomParticipant) => {
            if (!updatedRates[participant.id]) {
              updatedRates[participant.id] = {};
              specializations.forEach(spec => {
                updatedRates[participant.id][spec] = 0;
              });
            }
          });
          return updatedRates;
        });
      }
    } catch (error) {
      console.error('Error fetching custom participants:', error);
    }
  };

  const handleAddCustomParticipant = async () => {
    if (!newParticipantName.trim() || !biddingSheetId) return;
    
    try {
      const token = localStorage.getItem('spellbook_token');
      const response = await fetch(`${API_CONFIG.API_URL}/bidding/${biddingSheetId}/custom-participants`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ name: newParticipantName.trim() })
      });

      if (response.ok) {
        const newParticipant = await response.json();
        setCustomParticipants(prev => [...prev, newParticipant]);
        
        // Initialize rates for new participant
        setRates(prev => ({
          ...prev,
          [newParticipant.id]: specializations.reduce((acc, spec) => ({ ...acc, [spec]: 0 }), {})
        }));
        
        setNewParticipantName('');
      } else {
        const errorData = await response.json();
        alert(errorData.error || 'Failed to add participant');
      }
    } catch (error) {
      console.error('Error adding custom participant:', error);
      alert('Failed to add participant');
    }
  };

  const handleDeleteCustomParticipant = (participantId: string) => {
    setDeleteParticipantModal({ isOpen: true, participantId });
  };
  
  const confirmDeleteParticipant = async () => {
    const { participantId } = deleteParticipantModal;
    setDeleteParticipantModal({ isOpen: false, participantId: '' });
    
    try {
      const token = localStorage.getItem('spellbook_token');
      const response = await fetch(`${API_CONFIG.API_URL}/bidding/custom-participants/${participantId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setCustomParticipants(prev => prev.filter(p => p.id !== participantId));
        setRates(prev => {
          const updated = { ...prev };
          delete updated[participantId];
          return updated;
        });
      }
    } catch (error) {
      console.error('Error deleting custom participant:', error);
    }
  };

  const handleRateChange = (artistId: string, specialization: string, value: number) => {
    console.log(`Rate change for artist ${artistId}, spec ${specialization}: ${value}`);
    setRates(prev => ({
      ...prev,
      [artistId]: {
        ...(prev[artistId] || {}),
        [specialization]: value
      }
    }));
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('spellbook_token');
      
      // Get IDs of custom participants to exclude them from project rates
      const customParticipantIds = new Set(customParticipants.map(p => p.id));
      
      // Convert matrix to flat array format for API (only for regular artists, not custom participants)
      const ratesArray: Array<{artistId: string, specialization: string, hourlyRate: number}> = [];
      
      Object.keys(rates).forEach(artistId => {
        // Skip custom participants - their rates are managed per bidding sheet
        if (customParticipantIds.has(artistId)) {
          console.log(`⏭️ Skipping custom participant rates: ${artistId}`);
          return;
        }
        
        Object.keys(rates[artistId]).forEach(specialization => {
          ratesArray.push({
            artistId,
            specialization,
            hourlyRate: rates[artistId][specialization]
          });
        });
      });

      console.log('📊 Saving rates (artists only):', ratesArray);

      const response = await fetch(`${API_CONFIG.API_URL}/projects/${projectId}/rates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ rates: ratesArray })
      });

      if (response.ok) {
        console.log('✅ Rates saved successfully');
        // Update parent with new specializations
        onSpecializationsChange(specializations);
        // Notify parent to refresh assignments with new hourlyRate
        if (onRatesSaved) {
          onRatesSaved();
        }
        onClose();
      } else {
        const errorText = await response.text();
        console.error('❌ Failed to save rates:', response.status, errorText);
      }
    } catch (error) {
      console.error('❌ Error saving rates:', error);
    }
  };

  const handleAddSpecialization = () => {
    if (newSpecialization && !specializations.includes(newSpecialization)) {
      const updatedSpecs = [...specializations, newSpecialization];
      setSpecializations(updatedSpecs);
      
      // Add new specialization column to rates matrix (initialize with 0)
      const updatedRates = { ...rates };
      Object.keys(updatedRates).forEach(artistId => {
        updatedRates[artistId][newSpecialization] = 0;
      });
      setRates(updatedRates);
      
      setNewSpecialization('');
      setShowAddSpec(false);
    }
  };

  const handleRemoveSpecialization = (spec: string) => {
    setDeleteSpecModal({ isOpen: true, specName: spec });
  };
  
  const confirmDeleteSpec = () => {
    const { specName } = deleteSpecModal;
    setDeleteSpecModal({ isOpen: false, specName: '' });
    
    const updatedSpecs = specializations.filter(s => s !== specName);
      setSpecializations(updatedSpecs);
      
      // Remove specialization column from rates matrix
      const updatedRates = { ...rates };
      Object.keys(updatedRates).forEach(artistId => {
      delete updatedRates[artistId][specName];
      });
      setRates(updatedRates);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('bidding.artistRates.title')}
      size="xl"
    >
      <div className="space-y-4">
        {/* Info */}
        <div className="p-3 bg-blue-500/10 border border-blue-500/50 rounded-lg text-blue-400 text-sm">
          <ICONS.Info className="inline w-4 h-4 mr-2" />
          {t('bidding.artistRates.description')}
        </div>

        {/* Color-coding legend */}
        {activeTable === 'revenue' && (
          <div className="flex items-center space-x-4 text-xs text-gray-400">
            <span className="flex items-center">
              <span className="w-4 h-4 border border-gray-700 rounded mr-1.5 bg-gray-800"></span>
              {t('bidding.artistRates.globalRate')}
            </span>
            <span className="flex items-center">
              <span className="w-4 h-4 border-2 border-amber-500/70 rounded mr-1.5 bg-gray-800 relative">
                <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-amber-500 rounded-full"></span>
              </span>
              {t('bidding.artistRates.customRate')}
            </span>
          </div>
        )}

        {/* Specializations Management */}
        <div className="border border-gray-700 rounded-lg p-4 bg-gray-800/30">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-white flex items-center">
              <ICONS.Settings className="w-4 h-4 mr-2" />
              {t('bidding.artistRates.projectSpecializations')}
            </h3>
            {showAddSpec ? (
              <div className="flex items-center space-x-2">
                <select
                  value={newSpecialization}
                  onChange={(e) => setNewSpecialization(e.target.value)}
                  className="px-3 py-1 bg-gray-800 border border-gray-700 rounded text-white text-sm"
                >
                  <option value="">{t('common.select')}</option>
                  {PIPELINE_SPECIALIZATIONS.filter(spec => !specializations.includes(spec)).map(spec => (
                    <option key={spec} value={spec}>{spec}</option>
                  ))}
                </select>
                <button
                  onClick={handleAddSpecialization}
                  className="p-1 bg-green-600 hover:bg-green-700 rounded text-white"
                  disabled={!newSpecialization}
                >
                  <ICONS.Check className="w-4 h-4" />
                </button>
                <button
                  onClick={() => { setShowAddSpec(false); setNewSpecialization(''); }}
                  className="p-1 bg-gray-600 hover:bg-gray-700 rounded text-white"
                >
                  <ICONS.X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowAddSpec(true)}
                className="px-3 py-1 bg-indigo-600 hover:bg-indigo-700 rounded text-white text-sm flex items-center space-x-1"
              >
                <ICONS.Plus className="w-4 h-4" />
                <span>{t('common.add')}</span>
              </button>
            )}
          </div>
          
          <div className="flex flex-wrap gap-2">
            {specializations.map(spec => (
              <div key={spec} className="flex items-center space-x-1 px-3 py-1 bg-indigo-600/20 border border-indigo-500/50 rounded-lg text-indigo-300">
                <span className="text-sm font-medium">{spec}</span>
                <button
                  onClick={() => handleRemoveSpecialization(spec)}
                  className="hover:text-red-400 transition-colors"
                  title={t('bidding.artistRates.deleteSpecTitle')}
                >
                  <ICONS.X className="w-3 h-3" />
                </button>
              </div>
            ))}
            {specializations.length === 0 && (
              <p className="text-gray-400 text-sm">{t('bidding.artistRates.noSpecializations')}</p>
            )}
          </div>
        </div>

        {/* Matrix Table */}
        {loading ? (
          <div className="flex items-center justify-center p-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500" />
          </div>
        ) : (
          <div className="max-h-[60vh] overflow-auto">
            {/* Header for artists table */}
            <div className="mb-3 px-2">
              <h3 className="text-sm font-semibold text-gray-300">
                {t('bidding.artistRates.artistsTitle') || 'CG artists'}
              </h3>
            </div>
            <table className="w-full text-sm border-collapse">
              <thead className="text-xs text-gray-400 uppercase bg-gray-800/50 sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left border border-gray-700">
                    {t('artistForm.name')}
                  </th>
                  {specializations.map(spec => (
                    <th key={spec} className="px-4 py-3 text-center border border-gray-700">
                      {spec}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* Regular Artists */}
                {artists.map((artist) => (
                  <tr key={artist.id} className="hover:bg-gray-800/30">
                    <td className="px-4 py-3 text-white font-medium border border-gray-700">
                      {artist.name}
                      {artist.specialization && artist.specialization !== 'Virtual' && (
                        <span className="text-xs text-gray-400 ml-2">({artist.specialization})</span>
                      )}
                    </td>
                    {specializations.map(spec => {
                      const currentRate = rates[artist.id]?.[spec] || 0;
                      const isCustom = isCustomRate(artist.id, spec, currentRate);
                      const globalRate = globalRates[artist.id]?.[spec];
                      return (
                        <td key={spec} className="px-2 py-2 text-center border border-gray-700">
                          <div className="relative">
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={currentRate}
                              onChange={(e) => handleRateChange(artist.id, spec, parseFloat(e.target.value) || 0)}
                              className={getRateInputClass(artist.id, spec, currentRate)}
                              title={isCustom && globalRate !== undefined ? t('bidding.artistRates.globalRateLabel', { rate: globalRate }) : undefined}
                            />
                            {isCustom && (
                              <span className="absolute -top-1 -right-1 w-2 h-2 bg-amber-500 rounded-full" title={t('bidding.artistRates.customRate')} />
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}

                {/* Custom Participants */}
                {customParticipants.length > 0 && (
                  <tr className="bg-gray-800/20">
                    <td colSpan={specializations.length + 1} className="px-4 py-2 text-xs text-gray-400 font-semibold border border-gray-700">
                      {t('bidding.artistRates.customParticipants') || 'Unregistered Participants'}
                    </td>
                  </tr>
                )}
                {customParticipants.map((participant) => (
                  <tr key={participant.id} className="hover:bg-gray-800/30">
                    <td className="px-4 py-3 text-white font-medium border border-gray-700">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div 
                            className="w-3 h-3 rounded-full mr-2"
                            style={{ backgroundColor: participant.color }}
                          />
                          {participant.name}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleDeleteCustomParticipant(participant.id)}
                          className="text-red-400 hover:text-red-300 transition-colors"
                          title={t('common.delete')}
                        >
                          <ICONS.Trash className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                    {specializations.map(spec => (
                      <td key={spec} className="px-2 py-2 text-center border border-gray-700">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={rates[participant.id]?.[spec] || 0}
                          onChange={(e) => handleRateChange(participant.id, spec, parseFloat(e.target.value) || 0)}
                          className="w-20 px-2 py-1 bg-gray-800 border border-gray-700 rounded text-white text-center text-xs focus:outline-none focus:border-indigo-500"
                        />
                      </td>
                    ))}
                  </tr>
                ))}

                {/* Add New Custom Participant Row */}
                {biddingSheetId && (
                  <tr className="bg-indigo-500/5 border-t-2 border-indigo-500/30">
                    <td className="px-4 py-3 border border-gray-700">
                      <div className="flex items-center space-x-2">
                        <input
                          type="text"
                          value={newParticipantName}
                          onChange={(e) => setNewParticipantName(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && handleAddCustomParticipant()}
                          placeholder={t('bidding.artistRates.addParticipantPlaceholder') || 'Enter name...'}
                          className="flex-1 px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-white text-sm focus:outline-none focus:border-indigo-500"
                        />
                        <button
                          type="button"
                          onClick={handleAddCustomParticipant}
                          disabled={!newParticipantName.trim()}
                          className="p-1.5 bg-indigo-600 hover:bg-indigo-500 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded transition-colors"
                          title={t('common.add')}
                        >
                          <ICONS.Plus className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                    {specializations.map(spec => (
                      <td key={spec} className="px-2 py-2 text-center border border-gray-700 text-gray-600">
                        —
                      </td>
                    ))}
                  </tr>
                )}
              </tbody>
            </table>

            {artists.length === 0 && (
              <div className="text-center py-12 text-gray-400">
                <ICONS.User className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>{t('bidding.artistRates.noArtists')}</p>
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-700">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
            disabled={loading}
          >
            {t('common.save')}
          </button>
        </div>
      </div>
      
      {/* Delete Participant Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteParticipantModal.isOpen}
        onClose={() => setDeleteParticipantModal({ isOpen: false, participantId: '' })}
        onConfirm={confirmDeleteParticipant}
        title={t('common.confirmDelete')}
        message={t('bidding.artistRates.deleteParticipantConfirm')}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        type="danger"
      />
      
      {/* Delete Specialization Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteSpecModal.isOpen}
        onClose={() => setDeleteSpecModal({ isOpen: false, specName: '' })}
        onConfirm={confirmDeleteSpec}
        title={t('bidding.artistRates.deleteSpecTitle')}
        message={`${t('bidding.artistRates.deleteSpecConfirm')} "${deleteSpecModal.specName}"? ${t('bidding.artistRates.deleteSpecWarning')}`}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        type="danger"
      />
    </Modal>
  );
};

export default ArtistRatesModal;
